import React, { useState, useEffect } from 'react';
import { FileText, ChevronRight, Check, Lightbulb, AlertCircle, Shield } from 'lucide-react';
import SubmissionWizard from '../components/SubmissionWizard';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import API_ENDPOINTS from '../config/api';

const Submit = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [submissionType, setSubmissionType] = useState(null);
  const [showBulkPanel, setShowBulkPanel] = useState(false);
  const [isVerified, setIsVerified] = useState(null); // null = loading, true = verified, false = not verified
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { getToken, userId } = useAuth();
  const key = new URLSearchParams(location.search).get('new') || 'default';

  // Check user verification status on component mount
  useEffect(() => {
    const checkVerificationStatus = async () => {
      if (!userId) {
        setIsVerified(false);
        return;
      }

      try {
        const token = await getToken();
        const response = await fetch(API_ENDPOINTS.PROFILE_GET(userId), {
          headers: {
            'Authorization': token ? `Bearer ${token}` : undefined
          }
        });

        if (response.ok) {
          const data = await response.json();
          const verified = data.is_verified === 1 || data.is_verified === true;
          setIsVerified(verified);
          
          // Show modal if not verified
          if (!verified) {
            setShowVerificationModal(true);
          }
        } else {
          // If profile doesn't exist, user is not verified
          setIsVerified(false);
          setShowVerificationModal(true);
        }
      } catch (error) {
        console.error('Error checking verification status:', error);
        setIsVerified(false);
        setShowVerificationModal(true);
      }
    };

    checkVerificationStatus();
  }, [userId, getToken]);

  // Reset state when key changes (i.e., when user clicks Submit in header)
  useEffect(() => {
    setCurrentStep(0);
    setSubmissionType(null);
    setShowBulkPanel(false);
  }, [key]);

  const handleTypeSelection = (type) => {
    setSubmissionType(type);
    setCurrentStep(1);
  };

  const renderTypeSelection = () => (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Submit Your Research
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-400">
          Choose how you'd like to contribute to the scientific community
        </p>
      </div>

      <div className="flex justify-center mb-12">
        {/* Paper Card */}
        <div 
          onClick={() => handleTypeSelection('paper')}
          className="card p-8 cursor-pointer hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-primary-200 dark:hover:border-primary-800 group mr-8"
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
              <FileText className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Research Paper
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Submit completed research with findings, methodology, and conclusions. 
              Undergo peer review for publication.
            </p>
            <div className="text-left space-y-2 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center">
                <Check className="h-4 w-4 text-green-500 mr-2" />
                Peer review process
              </div>
              <div className="flex items-center">
                <Check className="h-4 w-4 text-green-500 mr-2" />
                DOI assignment
              </div>
              <div className="flex items-center">
                <Check className="h-4 w-4 text-green-500 mr-2" />
                Citation tracking
              </div>
              <div className="flex items-center">
                <Check className="h-4 w-4 text-green-500 mr-2" />
                Version control
              </div>
            </div>
          </div>
        </div>

        {/* Proposal Card */}
        <div 
          onClick={() => handleTypeSelection('proposal')}
          className="card p-8 cursor-pointer hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-primary-200 dark:hover:border-primary-800 group"
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
              <Lightbulb className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Research Proposal
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Share research ideas, hypotheses, and planned methodologies. 
              Get community feedback before starting.
            </p>
            <div className="text-left space-y-2 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center">
                <Check className="h-4 w-4 text-blue-500 mr-2" />
                Community feedback
              </div>
              <div className="flex items-center">
                <Check className="h-4 w-4 text-blue-500 mr-2" />
                Collaboration opportunities
              </div>
              <div className="flex items-center">
                <Check className="h-4 w-4 text-blue-500 mr-2" />
                Implementation tracking
              </div>
              <div className="flex items-center">
                <Check className="h-4 w-4 text-blue-500 mr-2" />
                Early validation
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk/API Options */}
      <div className="text-center">
        <button 
          onClick={() => setShowBulkPanel(true)}
          className="text-primary-600 hover:text-primary-700 font-medium flex items-center mx-auto"
        >
          Need to submit multiple items? Try bulk upload or API
          <ChevronRight className="ml-1 h-4 w-4" />
        </button>
      </div>

      {/* Bulk Panel */}
      {showBulkPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-2xl w-full mx-4">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Bulk Upload & API Options
            </h3>
            
            <div className="space-y-6">
              <div className="card p-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  CSV Bulk Upload
                </h4>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Upload multiple submissions using a CSV file. Perfect for large datasets or batch submissions.
                </p>
                <button className="btn-primary">
                  Download CSV Template
                </button>
              </div>

              <div className="card p-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  REST API
                </h4>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Integrate directly with our API for programmatic submissions. Great for automated workflows.
                </p>
                <div className="bg-gray-100 dark:bg-gray-700 rounded p-3 text-sm font-mono mb-4">
                  curl -X POST https://api.aixiv.org/v2/submissions \<br/>
                  &nbsp;&nbsp;-H "Authorization: Bearer YOUR_API_KEY" \<br/>
                  &nbsp;&nbsp;-H "Content-Type: application/json" \<br/>
                  &nbsp;&nbsp;-d @submission.json
                </div>
                <button className="btn-primary">
                  View API Documentation
                </button>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <button 
                onClick={() => setShowBulkPanel(false)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Verification Modal */}
        {showVerificationModal && isVerified === false && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-lg w-full mx-4 shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center">
                  <Shield className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                </div>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                Account Verification Required
              </h3>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-yellow-800 dark:text-yellow-200">
                    <p className="font-medium mb-2">You cannot submit papers until your account is verified.</p>
                    <p>To verify your academic status, please complete your profile with:</p>
                  </div>
                </div>
              </div>

              {/* Requirements List */}
              <div className="space-y-3 mb-8">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                    <span className="text-primary-600 dark:text-primary-400 text-sm font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Institutional Email</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Your official university or research institution email</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-6 h-6 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                    <span className="text-primary-600 dark:text-primary-400 text-sm font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Academic Identifiers</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">At least one of: ORCID, Google Scholar, or OpenReview ID</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-6 h-6 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                    <span className="text-primary-600 dark:text-primary-400 text-sm font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Academic Information</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Your academic status and research area</p>
                  </div>
                </div>
              </div>              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={() => navigate(`/profile/me`)}
                  className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
                >
                  Complete Profile
                  <ChevronRight className="ml-2 h-5 w-5" />
                </button>
                <button
                  onClick={() => setShowVerificationModal(false)}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  Later
                </button>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
                This helps us maintain the quality and integrity of our academic platform
              </p>
            </div>
          </div>
        )}

        {/* Main Content - Show loading state while checking verification */}
        {isVerified === null ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Checking verification status...</p>
            </div>
          </div>
        ) : isVerified === false ? (
          // Show type selection but disable interaction
          <div className="relative">
            <div className="absolute inset-0 bg-gray-100 dark:bg-gray-800 bg-opacity-60 z-10 flex items-center justify-center">
              <div className="text-center bg-white dark:bg-gray-900 rounded-lg p-8 shadow-xl max-w-md">
                <Shield className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Verification Required
                </h3>                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Please complete your profile to submit papers
                </p>
                <button
                  onClick={() => navigate(`/profile/me`)}
                  className="btn-primary"
                >
                  Go to Profile
                </button>
              </div>
            </div>
            {currentStep === 0 ? renderTypeSelection() : (
              <SubmissionWizard 
                key={key}
                type={submissionType}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                onBack={() => {
                  if (currentStep === 1) {
                    setCurrentStep(0);
                    setSubmissionType(null);
                  } else {
                    setCurrentStep(currentStep - 1);
                  }
                }}
              />
            )}
          </div>
        ) : (
          // User is verified, show normal content
          <>
            {currentStep === 0 ? renderTypeSelection() : (
              <SubmissionWizard 
                key={key}
                type={submissionType}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                onBack={() => {
                  if (currentStep === 1) {
                    setCurrentStep(0);
                    setSubmissionType(null);
                  } else {
                    setCurrentStep(currentStep - 1);
                  }
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Submit;
